import firebase from "firebase/compat/app";
import { initDb, getZooId } from "../tools/conf";

/**
 * Data connector for in-app note notifications (collection `noteNotifications`).
 *
 * Documents are created server-side by the `sendNoteNotifications` Cloud
 * Function for users whose `noteNotificationChannel === "inApp"`. The client
 * only reads its own notifications and acknowledges them (status flip).
 */
export class NotificationDatabaseConnector {
  /**
   * Resolve the current user id from localStorage (robust to the various
   * historical shapes), mirroring TaskNotificationDropdown.
   * @returns {string|null}
   */
  _getCurrentUserId() {
    let userId = null;
    const userDataString = localStorage.getItem("userData");
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        userId = userData.userId || userData.uid || userData.id;
      } catch (e) {
        console.error("[NoteNotifications] Error parsing userData:", e);
      }
    }
    if (!userId) {
      userId = localStorage.getItem("userId") || localStorage.getItem("uid");
    }
    return userId || null;
  }

  /**
   * Real-time listener on the current user's note notifications.
   * Resolves to an unsubscribe function (always callable).
   * @param {(notifications: Array<Object>) => void} onUpdate
   * @returns {Promise<() => void>}
   */
  async subscribeMyNoteNotifications(onUpdate) {
    try {
      const db = initDb();
      const zooId = await getZooId();
      const userId = this._getCurrentUserId();

      if (!userId) {
        console.error("[NoteNotifications] No user ID found for listener");
        return () => {};
      }

      const query = db
        .collection("noteNotifications")
        .where("userId", "==", userId)
        .where("zooId", "==", zooId)
        .orderBy("createdAt", "desc");

      return query.onSnapshot(
        (snapshot) => {
          const notifications = [];
          snapshot.forEach((doc) => {
            notifications.push({ id: doc.id, ...doc.data() });
          });
          onUpdate(notifications);
        },
        (error) => {
          console.error("[NoteNotifications] Listener error:", error);
        }
      );
    } catch (error) {
      console.error("[NoteNotifications] Error setting up listener:", error);
      return () => {};
    }
  }

  /**
   * One-time fetch of the current user's note notifications (newest first).
   * @returns {Promise<Array<Object>>}
   */
  async getMyNoteNotifications() {
    try {
      const db = initDb();
      const zooId = await getZooId();
      const userId = this._getCurrentUserId();
      if (!userId) return [];

      const snapshot = await db
        .collection("noteNotifications")
        .where("userId", "==", userId)
        .where("zooId", "==", zooId)
        .orderBy("createdAt", "desc")
        .get();

      const notifications = [];
      snapshot.forEach((doc) => {
        notifications.push({ id: doc.id, ...doc.data() });
      });
      return notifications;
    } catch (error) {
      console.error("[NoteNotifications] Error fetching notifications:", error);
      return [];
    }
  }

  /**
   * Acknowledge a set of notifications by id (status -> "acknowledged").
   * Commits in chunks of 500 (Firestore batch limit).
   * @param {Array<string>} ids
   * @returns {Promise<{success: boolean, count: number}>}
   */
  async acknowledgeNotifications(ids) {
    if (!Array.isArray(ids) || ids.length === 0) {
      return { success: true, count: 0 };
    }

    try {
      const db = initDb();
      const acknowledgedAt = firebase.firestore.FieldValue.serverTimestamp();
      const chunkSize = 500;

      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const batch = db.batch();
        chunk.forEach((id) => {
          const ref = db.collection("noteNotifications").doc(id);
          batch.update(ref, { status: "acknowledged", acknowledgedAt });
        });
        await batch.commit();
      }

      return { success: true, count: ids.length };
    } catch (error) {
      console.error("[NoteNotifications] Error acknowledging:", error);
      return { success: false, count: 0 };
    }
  }

  /**
   * Acknowledge every unread notification of the current user.
   * Fetches the user's notifications and filters "unread" in memory to avoid
   * an extra composite index.
   * @returns {Promise<{success: boolean, count: number}>}
   */
  async acknowledgeAll() {
    const all = await this.getMyNoteNotifications();
    const unreadIds = all
      .filter((n) => n.status !== "acknowledged")
      .map((n) => n.id);
    return this.acknowledgeNotifications(unreadIds);
  }
}

export default NotificationDatabaseConnector;
